import { BannerType } from '@/models';
import { ReactNode, JSX } from 'react';

import styles from './Tile.module.scss';
import Banner from '@/components/common/Banner';
import { BANNER_TYPE } from '@/types';

type contentType = {
  label?: string;
  paragraph?: ReactNode;
  subParagraph?: string;
  component?: ReactNode;
}

type TileProps = {
  content: contentType;
  trailingContent?: ReactNode;
  leadingContent?: ReactNode;
  informativeBanner?: BannerType;
  borderless?: boolean;
  selected?: boolean;
  onClick?: (event: any) => void;
  disabled?: boolean;
  centered?: boolean;
  truncated?: boolean;
  inlineText?: boolean;
  littleGap?: boolean;
  inactive?: boolean;
  alignToTop?: (ref: HTMLDivElement) => void;
  padding?: 'none' | 'sm' | 'md';
  subParagraphPosition?: 'top' | 'bottom';
  className?: string;
  dataRbf?: string;
}

const Tile = (props: TileProps): JSX.Element => {
  const {
    content,
    leadingContent = null,
    trailingContent = null,
    informativeBanner = null,
    borderless = false,
    selected,
    onClick,
    disabled = false,
    centered = false,
    truncated = false,
    littleGap = false,
    padding = 'sm',
    inlineText = false,
    subParagraphPosition = 'bottom',
    className = '',
    inactive = false,
    alignToTop = undefined,
    dataRbf
  } = props;

  const containerClassName = [
    className,
    styles.container,
    borderless && styles['borderless'],
    centered && styles['centered'],
    selected && styles['selected'],
    disabled && styles['disabled'],
    inlineText && styles['no-wrap'],
    littleGap && styles['little-gap'],
    content?.component && styles['grid-display'],
    padding === 'sm' && styles['padding-sm'],
    padding === 'md' && styles['padding-md'],
    padding === 'none' && styles['padding-none'],
    inactive && styles['inactive'],
    alignToTop && styles['scroll-align']
  ].filter(Boolean).join(' ');

  const computedTextClassName = [
    styles.text,
    centered && styles.centered,
    truncated && styles.truncated,
    subParagraphPosition === 'top' && styles['paragraph-top']
  ].filter(Boolean).join(' ');

  return (
    <div
      data-rbf={dataRbf}
      className={containerClassName}
      ref={alignToTop}
      data-test-id="tile"
      onClick={onClick}>
      <div className={styles['leading-center-content']}>
        {leadingContent && <div data-test-id="tile-leading-content">{leadingContent}</div>}
        <div className={computedTextClassName} data-test-id="tile-text">
          {content?.label && <span className={styles.label} data-test-id="tile-label">{content.label}</span>}
          {content?.paragraph && (
            <div className={styles.paragraph} data-test-id="tile-paragraph">
              {content.paragraph}
            </div>
          )}
          {content?.subParagraph && (
            <span className={styles['sub-paragraph']} data-test-id="tile-sub-paragraph">{content.subParagraph}</span>
          )}
        </div>
      </div>

      {content?.component && selected && (
        <section className={styles.expanded}>
          {content.component}
          {informativeBanner && (
            <div className={styles.banner}>
              <Banner type={BANNER_TYPE.INFORMATION} title={informativeBanner.title} content={informativeBanner.paragraph} />
            </div>
          )}
        </section>
      )}

      {trailingContent && (
        <div
          className={disabled ? styles['trailing-content-disabled'] : styles['trailing-content-available']}
          data-test-id="tile-trailing-content"
        >
          {trailingContent}
        </div>
      )}
    </div>
  );
};

export default Tile;
